import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

/**
 * «Jobb smartere» inline hint: one line at the place a manual workaround
 * happens (booking form, journal editor, invoice step, chip field), shown only
 * when GET /api/modulerecommendations/ recommends that module for the clinic.
 * Hidden under the md breakpoint so phone forms never shift. Also exports the
 * shared price line and action buttons used by the frontpage card.
 * Design: docs/superpowers/specs/2026-11-04-module-recommendations-design.md
 */

function PriceLine({ recommendation }) {
    const { price = 0 } = recommendation;
    if (price > 0) {
        return <Text id='moduleRecommendation.pricePerMonth' fields={{ price }}>{price} kr/month</Text>;
    }
    return <Text id='moduleRecommendation.included'>Included</Text>;
}

/**
 * «Les mer» (help article, when the catalogue has one), «Aktiver» (opens the
 * confirm dialog on /dyrejournal-clinic via ?enable=) and «Ikke nå» (snooze
 * 60 days). The two last only when the backend says canEnable.
 */
function RecommendationActions({ stores, recommendation, buttonClass = 'btn btn-sm' }) {
    const { moduleRecommendationStore } = stores;
    const {
        module, helpArticle = 0, canEnable = false,
    } = recommendation;
    // The hint sits inside footer drawers (booking, journal, payment, animal) that
    // survive a route change, so close them before navigating or the new page
    // would open underneath the drawer.
    const go = href => (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        moduleRecommendationStore.logEvent(module, 'clicked');
        if (stores.appState && typeof stores.appState.toggleDrawer === 'function') {
            stores.appState.toggleDrawer(false, 1);
        }
        route(href);
    };
    const snooze = () => moduleRecommendationStore.snooze(module);
    return (
        <span class='smart-tip-actions'>
            {helpArticle > 0 && (
                <a
                    href={`/help/${helpArticle}`}
                    class={`${buttonClass} btn-outline-secondary rounded-pill me-2`}
                    onClick={go(`/help/${helpArticle}`)}
                >
                    <Text id='moduleRecommendation.readMore'>Read more</Text>
                </a>
            )}
            {canEnable && (
                <a
                    href={`/dyrejournal-clinic?enable=${module}`}
                    class={`${buttonClass} btn-primary rounded-pill me-2 smart-tip-enable`}
                    onClick={go(`/dyrejournal-clinic?enable=${module}`)}
                >
                    <Text id='moduleRecommendation.enable'>Enable</Text>
                </a>
            )}
            {canEnable && (
                <button type='button' class={`${buttonClass} btn-link text-body-secondary smart-tip-snooze`} onClick={snooze}>
                    <Text id='moduleRecommendation.notNow'>Not now</Text>
                </button>
            )}
        </span>
    );
}

@observer
class ModuleHint extends Component {
    componentDidMount() {
        const { moduleRecommendationStore, userStore } = this.props.stores;
        moduleRecommendationStore.ensureLoaded(userStore?.user?.customer);
    }

    render() {
        const { module, stores } = this.props;
        const { moduleRecommendationStore } = stores;
        const recommendation = moduleRecommendationStore.has(module);
        if (!recommendation) {
            return null;
        }
        moduleRecommendationStore.logEvent(module, 'shown');
        return (
            <div class='alert alert-info py-1 px-2 small mb-2 d-none d-md-flex align-items-center flex-wrap module-hint'>
                <i class='fa-duotone fa-lightbulb-on me-2' />
                <span class='me-2'>
                    <strong class='me-1'><Text id='moduleRecommendation.hintPrefix'>Tip:</Text></strong>
                    {recommendation.evidence}
                </span>
                <RecommendationActions stores={stores} recommendation={recommendation} buttonClass='btn btn-sm py-0' />
            </div>
        );
    }
}

export { PriceLine, RecommendationActions };
export default ModuleHint;
